const PERMISSION_KEYS = [
  'canDashboard',
  'canRealEstate',
  'canRealEstateHub',
  'canLeadGen',
  'canCheckedAreas',
  'canContacts',
  'canPropertiesMap',
  'canLandMap',
  'canRawData',
  'canMasterPortal',
  'canShortTermRentals',
  'canShortTermRentalsFinancials',
  'canShortTermRentalsCleaning',
  'canShortTermRentalsCleaningEdit',
  'canDocumentation',
  'canMyTime',
  'canTimeTracking',
  'canTimeTrackingOverview',
  'canAdmin',
  'canAdminDbBackup',
  'canUserManagement'
];

export const DEFAULT_PERMISSIONS = {
  canDashboard: true,
  canRealEstate: true,
  canRealEstateHub: true,
  canLeadGen: true,
  canCheckedAreas: true,
  canContacts: true,
  canPropertiesMap: true,
  canLandMap: true,
  canRawData: true,
  canMasterPortal: true,
  canShortTermRentals: true,
  canShortTermRentalsFinancials: true,
  canShortTermRentalsCleaning: true,
  canShortTermRentalsCleaningEdit: false,
  canDocumentation: false,
  canMyTime: false,
  canTimeTracking: false,
  canTimeTrackingOverview: false,
  canAdmin: false,
  canAdminDbBackup: false,
  canUserManagement: false
};

export const normalizePermissions = (permissions = {}) => {
  const normalized = { ...DEFAULT_PERMISSIONS };
  PERMISSION_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) {
      normalized[key] = Boolean(permissions[key]);
    }
  });

  if (normalized.canAdmin) {
    normalized.canAdminDbBackup = true;
    normalized.canUserManagement = true;
  }

  if (!normalized.canRealEstate) {
    normalized.canRealEstateHub = false;
    normalized.canLeadGen = false;
    normalized.canCheckedAreas = false;
    normalized.canContacts = false;
    normalized.canPropertiesMap = false;
    normalized.canLandMap = false;
    normalized.canRawData = false;
    normalized.canMasterPortal = false;
  }

  if (!normalized.canShortTermRentals) {
    normalized.canShortTermRentalsFinancials = false;
    normalized.canShortTermRentalsCleaning = false;
    normalized.canShortTermRentalsCleaningEdit = false;
  }

  if (!normalized.canShortTermRentalsCleaning) {
    normalized.canShortTermRentalsCleaningEdit = false;
  }

  if (!normalized.canMyTime) {
    normalized.canTimeTracking = false;
    normalized.canTimeTrackingOverview = false;
  }

  return normalized;
};

export const saveAuthProfile = (profile) => {
  if (!profile || typeof profile !== 'object') {
    localStorage.removeItem('authUserProfile');
    return;
  }

  const normalizedProfile = {
    username: profile.username || profile.user || '',
    displayName: profile.displayName || profile.username || profile.user || '',
    permissions: normalizePermissions(profile.permissions || {}),
    defaultRoute: profile.defaultRoute || null
  };

  localStorage.setItem('authUserProfile', JSON.stringify(normalizedProfile));
};

export const clearAuthProfile = () => {
  localStorage.removeItem('authUserProfile');
};

export const getStoredAuthProfile = () => {
  const raw = localStorage.getItem('authUserProfile');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      return {
        username: parsed.username || '',
        displayName: parsed.displayName || parsed.username || '',
        permissions: normalizePermissions(parsed.permissions || {}),
        defaultRoute: parsed.defaultRoute || null
      };
    } catch (err) {
      clearAuthProfile();
    }
  }

  const fallbackUser = localStorage.getItem('leadGenUser');
  if (fallbackUser) {
    return {
      username: String(fallbackUser).toLowerCase(),
      displayName: fallbackUser,
      permissions: normalizePermissions({
        canDashboard: true,
        canRealEstate: true,
        canRealEstateHub: true,
        canLeadGen: true,
        canCheckedAreas: true,
        canContacts: true,
        canPropertiesMap: true,
        canLandMap: true,
        canRawData: true,
        canMasterPortal: true,
        canDocumentation: true,
        canMyTime: true,
        canTimeTracking: true,
        canTimeTrackingOverview: true,
        canAdmin: true,
        canAdminDbBackup: true,
        canUserManagement: true
      })
    };
  }

  return {
    username: '',
    displayName: '',
    permissions: normalizePermissions({}),
    defaultRoute: null
  };
};

export const hasPermission = (permissionKey) => {
  const profile = getStoredAuthProfile();
  const permissions = profile.permissions || DEFAULT_PERMISSIONS;
  if (permissions.canAdmin) {
    return true;
  }
  return permissions[permissionKey] === true;
};

export const hasAnyPermission = (permissionKeys = []) => permissionKeys.some((key) => hasPermission(key));

// Maps each selectable "default landing page" route to the permission check required to access it.
export const DEFAULT_ROUTE_OPTIONS = [
  { value: '/', label: 'Dashboard', check: (p) => p.canDashboard },
  { value: '/real-estate', label: 'Real Estate Hub', check: (p) => p.canRealEstate && p.canRealEstateHub },
  { value: '/real-estate/lead-gen', label: 'Lead Gen', check: (p) => p.canRealEstate && p.canLeadGen },
  { value: '/real-estate/lead-gen/checked-areas', label: 'Checked Areas Map', check: (p) => p.canRealEstate && p.canCheckedAreas },
  { value: '/real-estate/contacts', label: 'Contacts', check: (p) => p.canRealEstate && p.canContacts },
  { value: '/real-estate/properties-map', label: 'Properties Map', check: (p) => p.canRealEstate && p.canPropertiesMap },
  { value: '/real-estate/land-map', label: 'Land Map', check: (p) => p.canRealEstate && p.canLandMap },
  { value: '/real-estate/raw', label: 'Raw Data', check: (p) => p.canRealEstate && p.canRawData },
  { value: '/real-estate/master-portal', label: 'Master Portal', check: (p) => p.canRealEstate && p.canMasterPortal },
  { value: '/short-term-rentals', label: 'Short Term Rentals Hub', check: (p) => p.canShortTermRentals },
  { value: '/short-term-rentals/financials', label: 'Short Term Rentals: Financials', check: (p) => p.canShortTermRentals && p.canShortTermRentalsFinancials },
  { value: '/short-term-rentals/cleaning', label: 'Short Term Rentals: Cleaning & Maintenance', check: (p) => p.canShortTermRentals && p.canShortTermRentalsCleaning },
  { value: '/documentation', label: 'Documentation', check: (p) => p.canDocumentation },
  { value: '/my-time', label: 'My Time', check: (p) => p.canMyTime },
  { value: '/my-time/activity-log', label: 'Activity Log', check: (p) => p.canMyTime },
  { value: '/my-time/time-tracking', label: 'Time Tracking', check: (p) => p.canTimeTracking },
  { value: '/my-time/time-tracking/all', label: 'Time Tracking Overview', check: (p) => p.canTimeTrackingOverview },
  { value: '/tasks', label: 'Tasks', check: (p) => p.canMyTime },
  { value: '/admin', label: 'Admin', check: (p) => p.canAdmin },
  { value: '/admin/db-backup', label: 'Admin DB Backup', check: (p) => p.canAdmin || p.canAdminDbBackup },
  { value: '/admin/users', label: 'User Management', check: (p) => p.canAdmin || p.canUserManagement }
];

export const getDefaultRoute = () => {
  const profile = getStoredAuthProfile();
  const permissions = profile.permissions;

  if (profile.defaultRoute) {
    const option = DEFAULT_ROUTE_OPTIONS.find((entry) => entry.value === profile.defaultRoute);
    if (option && option.check(permissions)) {
      return option.value;
    }
  }

  if (permissions.canDashboard) return '/';
  if (permissions.canRealEstate && permissions.canRealEstateHub) return '/real-estate';
  if (permissions.canLeadGen) return '/real-estate/lead-gen';
  if (permissions.canContacts) return '/real-estate/contacts';
  if (permissions.canPropertiesMap) return '/real-estate/properties-map';
  if (permissions.canLandMap) return '/real-estate/land-map';
  if (permissions.canMasterPortal) return '/real-estate/master-portal';
  if (permissions.canRawData) return '/real-estate/raw';
  if (permissions.canShortTermRentals) return '/short-term-rentals';
  if (permissions.canDocumentation) return '/documentation';
  if (permissions.canMyTime && permissions.canTimeTracking) return '/my-time/time-tracking';
  if (permissions.canMyTime && permissions.canTimeTrackingOverview) return '/my-time/time-tracking/all';
  if (permissions.canMyTime) return '/my-time';
  if (permissions.canAdminDbBackup) return '/admin/db-backup';
  if (permissions.canUserManagement) return '/admin/users';
  if (permissions.canAdmin) return '/admin';
  return '/login';
};
